import { sessionStatusStyles } from './constants';
import type { SessionGroup, ScheduleTab } from './types';
import { formatDuration, formatTimeRange, getInitials } from './utils';

type SessionsListProps = {
  data: {
    isLoading: boolean;
    groups: SessionGroup[];
    activeTab?: ScheduleTab;
  };
};

const isSessionJoinable = (sessionDate: string, sessionStart: string, sessionStatus: string): boolean => {
  // Always disable for Completed, Missed, Cancelled
  if (!['Active', 'Scheduled', 'Upcoming'].includes(sessionStatus)) {
    return false;
  }
  

  try {
    const now = new Date();
    const [year, month, day] = sessionDate.split('-').map(Number);
    const [hours, minutes] = sessionStart.split(':').map(Number);
    
    const sessionDateTime = new Date(year, month - 1, day, hours, minutes, 0);
    const timeUntilStart = sessionDateTime.getTime() - now.getTime();
    const minutesUntilStart = timeUntilStart / (1000 * 60);
    
    if (minutesUntilStart <= 15) {
      const hoursAfterStart = -minutesUntilStart / 60;
      if (hoursAfterStart > 24) {
        return false;
      }
      return true;
    }
    
    return false;
  } catch {
    return true; // Enable on error
  }
};

const getEmptyMessage = (tab: ScheduleTab | undefined) => {
  switch (tab) {
    case 'All':
      return 'No sessions found. Schedule your first session!';
    case 'Upcoming':
      return 'No upcoming sessions scheduled.';
    case 'Completed':
      return 'No completed sessions yet.';
    case 'Missed':
      return 'No missed sessions.';
    default:
      return 'No sessions for this view.';
  }
};

const SessionsList = ({ data }: SessionsListProps) => (
  <div className="flex flex-col gap-6">
    {data.isLoading ? (
      <div className="flex flex-col gap-4">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={`session-skeleton-${index}`}
            className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-100 bg-white px-4 py-4 shadow-sm"
          >
            <div className="min-w-[200px] space-y-2">
              <div className="h-4 w-32 rounded-full bg-slate-100" />
              <div className="h-3 w-16 rounded-full bg-slate-100" />
            </div>
            <div className="flex min-w-[200px] items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-slate-100" />
              <div className="space-y-2">
                <div className="h-4 w-24 rounded-full bg-slate-100" />
                <div className="h-3 w-20 rounded-full bg-slate-100" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-4 w-20 rounded-full bg-slate-100" />
              <div className="h-7 w-20 rounded-md bg-slate-100" />
              <div className="h-8 w-16 rounded-md bg-slate-100" />
            </div>
          </div>
        ))}
      </div>
    ) : (
      <>
        {data.groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <svg
              className="h-16 w-16 text-slate-300"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <p className="text-sm font-medium text-slate-500">{getEmptyMessage(data.activeTab)}</p>
          </div>
        ) : null}
        {data.groups.map((group) => (
          <div key={group.date} className="flex flex-col gap-3">
            <p className="text-sm font-semibold text-slate-400">{group.label}</p>
            <div className="flex flex-col gap-3">
              {group.sessions.map((session) => (
                <div
                  key={session.id}
                  className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-100 bg-white px-4 py-4 shadow-sm"
                >
                  <div className="min-w-[200px] flex justify-between">
                    <p className="text-sm font-semibold text-slate-900">
                      {formatTimeRange(session.start, session.end)}
                    </p>
                    <p className="text-xs text-slate-400">
                      {formatDuration(session.start, session.end)}
                    </p>
                  </div>
                  <div className="flex min-w-[200px] items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                      {getInitials(session.student)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {session.student}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-xs text-slate-400">{session.focus}</p>
                    {(() => {
                      const isJoinable = isSessionJoinable(session.date, session.start, session.status);
                      const displayStatus = isJoinable ? 'Active' : session.status;
                      return (
                        <span
                          className={`w-[90px] rounded-[6px] px-3 py-2 text-center text-xs font-semibold ${sessionStatusStyles[displayStatus]}`}
                        >
                          {displayStatus}
                        </span>
                      );
                    })()}
                    <button
                      type="button"
                      className="flex items-center gap-2 rounded-[6px] border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:enabled:bg-slate-50 disabled:cursor-not-allowed disabled:border-slate-100 disabled:text-slate-300 disabled:shadow-none"
                      disabled={!isSessionJoinable(session.date, session.start, session.status)}
                      onClick={() => {
                        if (session.joining_link) {
                          window.open(session.joining_link, '_blank');
                        }
                      }}
                    >
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M23 7l-7 5 7 5V7z" />
                        <rect x="1" y="5" width="15" height="14" rx="2" />
                      </svg>
                      Join
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </>
    )}
  </div>
);

export default SessionsList;
